import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';

interface Props {
  onSignedIn: () => void;
  /** Set when a session expired mid-use, rather than a plain first visit. */
  notice?: string | null;
}

export function LoginForm({ onSignedIn, notice }: Props) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.login(password);
      setPassword('');
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not sign in.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="w-full max-w-lg rounded-2xl border border-white/10 bg-surface p-5 shadow-2xl sm:p-8">
      <h1 className="m-0 text-2xl font-semibold tracking-tight">Kid Status</h1>
      <p className="mt-1.5 mb-0 text-sm text-ink-muted">
        Enter the household password to update the board.
      </p>

      {notice && (
        <p role="status" className="mt-4 mb-0 rounded-lg bg-surface-raised px-3 py-2 text-sm text-ink-muted">
          {notice}
        </p>
      )}

      <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-2.5">
        <label htmlFor="password" className="text-sm font-medium text-ink-muted">
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          autoFocus
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          className="w-full rounded-xl border border-white/10 bg-surface-raised px-4 py-3 text-ink outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
        />
        <button
          type="submit"
          disabled={busy}
          className="mt-1 min-h-11 cursor-pointer rounded-xl bg-sky-700 px-4 py-3 font-semibold text-white transition hover:brightness-110 disabled:cursor-progress disabled:opacity-55 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-300"
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        {error && (
          <p role="alert" className="mt-1 mb-0 text-sm text-rose-300">
            {error}
          </p>
        )}
      </form>
    </section>
  );
}
